/**
 * Strings of the users view (cards, list, detail panel, account actions), merged into `adm.users.ux` by `admin.ts`.
 * `admUsersEn` is typed against `admUsersPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const admUsersPl = {
  search: 'Szukaj',
  everyone: 'Wszyscy',
  you: 'to Ty',
  noManager: 'Bez przełożonego',
  team: 'Zespół: {name}',
  profile: 'Profil konta',
  menu: 'Akcje konta: {name}',
  menuProfile: 'Podgląd profilu',
  select: 'Zaznacz: {name}',
  copyEmail: 'Kopiuj e-mail',
  emailCopied: 'E-mail skopiowany',
  copyFailed: 'Nie udało się skopiować',
  copy: 'Kopiuj',
  state: { ACTIVE: 'Aktywne', PENDING: 'Czeka na logowanie', INACTIVE: 'Nieaktywne' },
  stateHint: {
    ACTIVE: 'Konto jest aktywne i użytkownik loguje się do systemu.',
    PENDING: 'Konto jest aktywne, ale użytkownik jeszcze się nie zalogował. Przekaż mu hasło.',
    INACTIVE: 'Konto jest zablokowane — użytkownik nie może się zalogować, a jego dane zostają w systemie.',
  },
  notActive: 'Każde konto poza aktywnym',
  login: { day: 'do 24 godz.', week: '1–7 dni', older: 'ponad 7 dni', never: 'nigdy' },
  on: 'Włączone',
  off: 'Wyłączone',
  mfaOn: 'Logowanie dwuetapowe włączone',
  mfaOff: 'Brak logowania dwuetapowego',
  mfaOnFilter: '2FA włączone',
  mfaOffFilter: '2FA wyłączone',
  newMonth: 'Nowe w tym miesiącu',
  sortLabel: 'Sortowanie',
  sort: { name: 'Nazwisko A–Z', name_desc: 'Nazwisko Z–A', role: 'Rola', login: 'Ostatnie logowanie', created: 'Najnowsze konta' },
  group: { role: 'Rola', state: 'Status', department: 'Dział', manager: 'Zespół', none: 'Bez grup' },
  viewGrid: 'Karty',
  viewList: 'Lista',
  fold: 'Zwiń grupy',
  unfold: 'Rozwiń grupy',
  shown: 'Pokazano {shown} z {total}',
  clearAll: 'Wyczyść filtry',
  noResults: 'Brak kont spełniających kryteria',
  noResultsHint: 'Zmień filtry albo wyszukaj inaczej.',
  truncated: 'Pokazano pierwsze {shown} z {total} kont — zawęź wyszukiwanie przez przełożonego lub rolę.',
  selected: 'Zaznaczono: {count}',
  selectAll: 'Zaznacz wszystkich',
  deselectAll: 'Odznacz wszystkich',
  export: 'Eksport CSV',
  exported: 'Wyeksportowano kont: {count}',
  csvActive: 'Aktywne',
  bulkActivated: 'Aktywowano kont: {count}',
  bulkDeactivated: 'Dezaktywowano kont: {count}',
  bulkPartial: 'Udało się {ok} z {total} kont',
  byRole: 'Konta wg ról',
  quick: 'Szybkie akcje',
  rolesLink: 'Role i uprawnienia',
  auditLink: 'Dziennik audytu',
  recent: 'Ostatnie logowania',
  noLogins: 'Nikt jeszcze się nie zalogował.',
  tab: { overview: 'Przegląd', account: 'Konto', permissions: 'Uprawnienia', security: 'Bezpieczeństwo', activity: 'Aktywność' },
  stats: { visits: 'Wizyty', tasks: 'Zadania otwarte', customers: 'Klienci', done: 'zakończone: {count}', leads: 'leady: {count}' },
  accountSince: 'Konto od',
  accountAge: '{date} · {days} dni',
  subordinates: 'Podwładni',
  created: 'Utworzono',
  updated: 'Ostatnia zmiana',
  scope: 'Zakres danych',
  sessions: 'Sesje',
  sessionsHint: 'zmiana hasła wylogowuje konto ze wszystkich urządzeń',
  ownAccount: 'To Twoje konto. Hasło i 2FA zmienisz w ustawieniach profilu; nie możesz sam siebie dezaktywować ani usunąć.',
  noAuditAccess: 'Do dziennika audytu potrzebne jest uprawnienie „Dziennik audytu”.',
  activityError: 'Nie udało się wczytać aktywności.',
  noActivity: 'To konto nie wykonało jeszcze żadnych operacji.',
  activityTotal: 'Operacje konta: {count} (najnowsze na górze)',
  setPassword: 'Ustaw nowe hasło',
  newPassword: 'Nowe hasło',
  setPasswordHint: 'Co najmniej {min} znaków. Konto zostanie wylogowane ze wszystkich urządzeń.',
  generate: 'Wygeneruj',
  reveal: 'Pokaż / ukryj hasło',
  passwordSet: 'Hasło zmienione, konto wylogowane',
  resetPassword: 'Wygeneruj hasło tymczasowe',
  resetPasswordBody: 'Obecne hasło przestanie działać, a konto zostanie wylogowane. Nowe hasło zobaczysz raz — przekaż je użytkownikowi bezpiecznym kanałem.',
  generatedBody: 'Hasło tymczasowe konta:',
  generatedHint: 'Hasło jest pokazywane tylko teraz i nie jest nigdzie zapisane w postaci jawnej.',
  passwordCopied: 'Hasło skopiowane',
  resetTwoFactor: 'Zresetuj 2FA',
  resetTwoFactorBody: 'Zdejmie logowanie dwuetapowe z tego konta (np. po zgubieniu telefonu). Użytkownik włączy je ponownie po zalogowaniu.',
  mfaReset: '2FA zresetowane',
  delete: 'Usuń konto',
  deleteBody: 'Konto zostanie usunięte bezpowrotnie. Usunąć można tylko konto bez danych (wizyt, zadań, klientów, leadów i podwładnych).',
  hasData: 'To konto ma dane w systemie, więc nie można go usunąć. Zdezaktywuj je zamiast usuwać.',
  deleted: 'Konto usunięte',
  activated: 'Konto aktywowane',
  deactivated: 'Konto dezaktywowane',
  confirm: { set: 'Zmień hasło', reset: 'Wygeneruj hasło', mfa: 'Zresetuj 2FA', delete: 'Usuń konto', link: 'Wyślij link' },
  createHint: 'Nowe konto zaloguje się podanym hasłem — przekaż je użytkownikowi.',
  sectionPerson: 'Dane osoby',
  sectionAccess: 'Rola i zespół',
  sectionPassword: 'Hasło początkowe',
  department: 'Dział',
  employeeId: 'Nr ewidencyjny',
  employeeIdAuto: 'Nadany automatycznie przy zapisie',
  employeeIdHint: 'Numer nadaje system przy zakładaniu konta. Nie można go wpisać ani zmienić.',
  noDepartment: 'Bez działu',
  emailTaken: 'Konto z tym adresem e-mail już istnieje.',
  mfaPending: '2FA do konfiguracji',
  mfaPendingLong: 'Wymagane — jeszcze nieskonfigurowane',
  mfaPendingBanner: 'Administrator wymaga 2FA, ale użytkownik jeszcze go nie skonfigurował. Do czasu konfiguracji może korzystać wyłącznie z ekranu włączenia 2FA.',
  mfaOnRequired: 'Włączone (wymagane)',
  mustChangeShort: 'Wymaga zmiany hasła',
  mustChangeBanner: 'Konto ma hasło tymczasowe — użytkownik zmieni je przy najbliższym logowaniu.',
  passwordState: 'Hasło',
  passwordSetState: 'Ustawione przez użytkownika',
  mustChange: 'Wymagaj zmiany hasła przy pierwszym logowaniu',
  sendActivation: 'Wyślij link aktywacyjny',
  sendReset: 'Wyślij link do zmiany hasła',
  sendActivationLabel: 'Wyślij e-mail z linkiem do ustawienia hasła',
  sendActivationHint: 'Użytkownik sam ustawi hasło (link ważny 72 godz.). Nie musisz go wymyślać ani przekazywać.',
  activationBody: 'Wyślemy na {email} link do ustawienia pierwszego hasła, ważny {hours} godz. Poprzednie linki przestaną działać.',
  resetLinkBody: 'Wyślemy na {email} link do ustawienia nowego hasła, ważny {hours} godz. Obecne hasło działa, dopóki użytkownik go nie użyje. Poprzednie linki przestaną działać.',
  linkEmailed: 'Wysłano wiadomość na {email}.',
  linkNotEmailed: 'Nie udało się wysłać wiadomości (poczta niedostępna). Przekaż użytkownikowi link ręcznie:',
  linkNotEmailedCopied: 'Poczta niedostępna — link skopiowano do schowka. Przekaż go użytkownikowi.',
  linkFailed: 'Konto utworzone, ale nie udało się przygotować linku. Wyślij go z menu konta.',
  linkInactive: 'Konto jest nieaktywne — najpierw je aktywuj.',
  linkCopied: 'Link skopiowany',
  linkHint: 'Link działa raz i wygasa po {hours} godz. Widzisz go tylko Ty.',
  require: 'Wymagaj 2FA',
  stopRequire: 'Zdejmij wymóg 2FA',
  requireOn: 'Wymóg 2FA włączony',
  requireOff: 'Wymóg 2FA zdjęty',
  bulkRequired: 'Wymóg 2FA włączony dla {changed} z {total} kont',
  bulkNotRequired: 'Wymóg 2FA zdjęty dla {changed} z {total} kont',
  bulkNoChange: 'Zaznaczone konta miały już takie ustawienie',
  requireMfaLabel: 'Wymagaj logowania dwuetapowego (2FA)',
  requireMfaHint: 'Do czasu skonfigurowania 2FA konto będzie mogło tylko je włączyć.',
} as const;

export const admUsersEn: Shape<typeof admUsersPl> = {
  search: 'Search',
  everyone: 'Everyone',
  you: 'you',
  noManager: 'No manager',
  team: 'Team: {name}',
  profile: 'Account profile',
  menu: 'Account actions: {name}',
  menuProfile: 'View profile',
  select: 'Select: {name}',
  copyEmail: 'Copy e-mail',
  emailCopied: 'E-mail copied',
  copyFailed: 'Could not copy',
  copy: 'Copy',
  state: { ACTIVE: 'Active', PENDING: 'Awaiting sign-in', INACTIVE: 'Inactive' },
  stateHint: {
    ACTIVE: 'The account is active and the user signs in to the system.',
    PENDING: 'The account is active but the user has not signed in yet. Hand over the password.',
    INACTIVE: 'The account is blocked — the user cannot sign in, and their data stays in the system.',
  },
  notActive: 'Any account but an active one',
  login: { day: 'within 24 h', week: '1–7 days', older: 'over 7 days', never: 'never' },
  on: 'On',
  off: 'Off',
  mfaOn: 'Two-factor sign-in enabled',
  mfaOff: 'No two-factor sign-in',
  mfaOnFilter: '2FA on',
  mfaOffFilter: '2FA off',
  newMonth: 'New this month',
  sortLabel: 'Sort',
  sort: { name: 'Last name A–Z', name_desc: 'Last name Z–A', role: 'Role', login: 'Last sign-in', created: 'Newest accounts' },
  group: { role: 'Role', state: 'Status', department: 'Department', manager: 'Team', none: 'No groups' },
  viewGrid: 'Cards',
  viewList: 'List',
  fold: 'Fold groups',
  unfold: 'Unfold groups',
  shown: 'Showing {shown} of {total}',
  clearAll: 'Clear filters',
  noResults: 'No accounts match',
  noResultsHint: 'Change the filters or search differently.',
  truncated: 'Showing the first {shown} of {total} accounts — narrow the search by manager or role.',
  selected: 'Selected: {count}',
  selectAll: 'Select all',
  deselectAll: 'Deselect all',
  export: 'Export CSV',
  exported: 'Accounts exported: {count}',
  csvActive: 'Active',
  bulkActivated: 'Accounts activated: {count}',
  bulkDeactivated: 'Accounts deactivated: {count}',
  bulkPartial: '{ok} of {total} accounts succeeded',
  byRole: 'Accounts by role',
  quick: 'Quick actions',
  rolesLink: 'Roles and permissions',
  auditLink: 'Audit log',
  recent: 'Latest sign-ins',
  noLogins: 'Nobody has signed in yet.',
  tab: { overview: 'Overview', account: 'Account', permissions: 'Permissions', security: 'Security', activity: 'Activity' },
  stats: { visits: 'Visits', tasks: 'Open tasks', customers: 'Customers', done: 'completed: {count}', leads: 'leads: {count}' },
  accountSince: 'Account since',
  accountAge: '{date} · {days} days',
  subordinates: 'Direct reports',
  created: 'Created',
  updated: 'Last change',
  scope: 'Data scope',
  sessions: 'Sessions',
  sessionsHint: 'changing the password signs the account out everywhere',
  ownAccount: 'This is your account. Change your password and 2FA in your profile settings; you cannot deactivate or delete yourself.',
  noAuditAccess: 'The audit log needs the “Audit log” permission.',
  activityError: 'Could not load the activity.',
  noActivity: 'This account has not performed any operations yet.',
  activityTotal: 'Account operations: {count} (newest first)',
  setPassword: 'Set a new password',
  newPassword: 'New password',
  setPasswordHint: 'At least {min} characters. The account is signed out everywhere.',
  generate: 'Generate',
  reveal: 'Show / hide password',
  passwordSet: 'Password changed, account signed out',
  resetPassword: 'Generate a temporary password',
  resetPasswordBody: 'The current password stops working and the account is signed out. You will see the new password once — pass it to the user through a safe channel.',
  generatedBody: 'Temporary password of the account:',
  generatedHint: 'The password is shown only now and is not stored anywhere in plain text.',
  passwordCopied: 'Password copied',
  resetTwoFactor: 'Reset 2FA',
  resetTwoFactorBody: 'Removes two-factor sign-in from this account (e.g. after losing the phone). The user enrols again after signing in.',
  mfaReset: '2FA reset',
  delete: 'Delete account',
  deleteBody: 'The account will be deleted permanently. Only an account without data (visits, tasks, customers, leads, direct reports) can be deleted.',
  hasData: 'This account has data in the system, so it cannot be deleted. Deactivate it instead.',
  deleted: 'Account deleted',
  activated: 'Account activated',
  deactivated: 'Account deactivated',
  confirm: { set: 'Change password', reset: 'Generate password', mfa: 'Reset 2FA', delete: 'Delete account', link: 'Send link' },
  createHint: 'The new account signs in with the given password — pass it to the user.',
  sectionPerson: 'Person',
  sectionAccess: 'Role and team',
  sectionPassword: 'Initial password',
  department: 'Department',
  employeeId: 'Employee number',
  employeeIdAuto: 'Assigned automatically on save',
  employeeIdHint: 'The system assigns the number when the account is created. It cannot be typed in or changed.',
  noDepartment: 'No department',
  emailTaken: 'An account with this e-mail already exists.',
  mfaPending: '2FA to set up',
  mfaPendingLong: 'Required — not set up yet',
  mfaPendingBanner: 'An administrator requires 2FA but the user has not set it up yet. Until then the account can only use the 2FA setup screen.',
  mfaOnRequired: 'On (required)',
  mustChangeShort: 'Must change password',
  mustChangeBanner: 'The account has a temporary password — the user changes it at the next sign-in.',
  passwordState: 'Password',
  passwordSetState: 'Set by the user',
  mustChange: 'Require a password change at the first sign-in',
  sendActivation: 'Send activation link',
  sendReset: 'Send password-change link',
  sendActivationLabel: 'E-mail a link to set the password',
  sendActivationHint: 'The user sets the password themselves (link valid for 72 h). You do not have to invent or hand it over.',
  activationBody: 'We will send {email} a link to set the first password, valid for {hours} h. Earlier links stop working.',
  resetLinkBody: 'We will send {email} a link to set a new password, valid for {hours} h. The current password keeps working until the user uses the link. Earlier links stop working.',
  linkEmailed: 'Message sent to {email}.',
  linkNotEmailed: 'The message could not be sent (mail unavailable). Pass the link to the user manually:',
  linkNotEmailedCopied: 'Mail unavailable — the link was copied to the clipboard. Pass it to the user.',
  linkFailed: 'The account was created, but the link could not be prepared. Send it from the account menu.',
  linkInactive: 'The account is inactive — activate it first.',
  linkCopied: 'Link copied',
  linkHint: 'The link works once and expires after {hours} h. Only you can see it.',
  require: 'Require 2FA',
  stopRequire: 'Stop requiring 2FA',
  requireOn: '2FA requirement turned on',
  requireOff: '2FA requirement removed',
  bulkRequired: '2FA required for {changed} of {total} accounts',
  bulkNotRequired: '2FA requirement removed for {changed} of {total} accounts',
  bulkNoChange: 'The selected accounts already had this setting',
  requireMfaLabel: 'Require two-factor sign-in (2FA)',
  requireMfaHint: 'Until 2FA is set up the account can only turn it on.',
};
